import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useWebMapContext } from '../webMap/WebMapContext';
import type { WidgetDataBinding, ActivityTimelineWidgetConfig } from './types';
import { CHART_COLOR_PALETTES } from './chartColorPalettes';

/* ── Helpers ── */

function formatTimestamp(ts: unknown): string {
  if (ts == null) return '';
  const d = new Date(String(ts));
  if (isNaN(d.getTime())) return String(ts);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function shortTime(ts: unknown): string {
  if (ts == null) return '';
  const d = typeof ts === 'number' ? new Date(ts) : new Date(String(ts));
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function dotColor(category: string | undefined, palette: string[], catIndex: Map<string, number>): string {
  if (!category) return '#78909c';
  const idx = catIndex.get(category.toLowerCase());
  if (idx != null) return palette[idx % palette.length];
  return '#78909c';
}

interface Props {
  widgetConfig?: ActivityTimelineWidgetConfig & { dataBinding?: WidgetDataBinding };
}

const ActivityTimelineWidget: React.FC<Props> = ({ widgetConfig }) => {
  const ctx = useWebMapContext();

  const tsField = widgetConfig?.timestampField || 'timestamp';
  const labelField = widgetConfig?.labelField || 'entity_id';
  const descField = widgetConfig?.descriptionField || '';
  const catField = widgetConfig?.categoryField || '';
  const maxItems = widgetConfig?.maxItems ?? 2000;
  const orientation = widgetConfig?.orientation ?? 'vertical';
  const viewMode = widgetConfig?.viewMode ?? 'timeline';
  const paletteId = widgetConfig?.colorPalette ?? 'default';
  const palette = CHART_COLOR_PALETTES[paletteId] ?? CHART_COLOR_PALETTES.default;

  const features = useMemo(() => {
    const layerId = widgetConfig?.dataBinding?.boundLayerId;
    if (!layerId || !ctx) return [];
    return ctx.getFeaturesForLayer(layerId);
  }, [widgetConfig?.dataBinding?.boundLayerId, ctx]);

  const sorted = useMemo(() => {
    const arr = [...features];
    arr.sort((a, b) => {
      const ta = new Date(String(a.properties?.[tsField] ?? '')).getTime() || 0;
      const tb = new Date(String(b.properties?.[tsField] ?? '')).getTime() || 0;
      return tb - ta;
    });
    return arr.slice(0, maxItems);
  }, [features, tsField, maxItems]);

  const catIndex = useMemo(() => {
    const map = new Map<string, number>();
    if (!catField) return map;
    let idx = 0;
    for (const f of sorted) {
      const c = String(f.properties?.[catField] ?? '').toLowerCase();
      if (c && !map.has(c)) { map.set(c, idx++); }
    }
    return map;
  }, [sorted, catField]);

  const timeRange = useMemo<[number, number]>(() => {
    if (sorted.length === 0) return [0, 1];
    let min = Infinity;
    let max = -Infinity;
    for (const f of sorted) {
      const t = new Date(String(f.properties?.[tsField] ?? '')).getTime();
      if (!isNaN(t)) {
        if (t < min) min = t;
        if (t > max) max = t;
      }
    }
    if (min === Infinity) return [0, 1];
    return [min, max];
  }, [sorted, tsField]);

  const [sliderValue, setSliderValue] = useState<[number, number]>([0, 100]);

  const windowRange = useMemo<[number, number]>(() => {
    const span = timeRange[1] - timeRange[0];
    return [
      timeRange[0] + (sliderValue[0] / 100) * span,
      timeRange[0] + (sliderValue[1] / 100) * span,
    ];
  }, [timeRange, sliderValue]);

  const windowItems = useMemo(() => {
    if (sliderValue[0] === 0 && sliderValue[1] === 100) return sorted;
    return sorted.filter((f) => {
      const t = new Date(String(f.properties?.[tsField] ?? '')).getTime();
      return !isNaN(t) && t >= windowRange[0] && t <= windowRange[1];
    });
  }, [sorted, sliderValue, tsField, windowRange]);

  const handleSliderChange = useCallback((_: unknown, val: number | number[]) => {
    setSliderValue(val as [number, number]);
  }, []);

  /* ── Stats ── */
  const stats = useMemo(() => {
    const catCounts = new Map<string, number>();
    const labelSet = new Set<string>();
    for (const f of windowItems) {
      const p = f.properties ?? {};
      labelSet.add(String(p[labelField] ?? ''));
      if (catField) {
        const c = String(p[catField] ?? 'Unknown');
        catCounts.set(c, (catCounts.get(c) ?? 0) + 1);
      }
    }
    const catArr: { label: string; count: number; color: string }[] = [];
    catCounts.forEach((count, label) => {
      catArr.push({ label, count, color: dotColor(label, palette, catIndex) });
    });
    catArr.sort((a, b) => b.count - a.count);
    return {
      totalEvents: windowItems.length,
      uniqueEntities: labelSet.size,
      categories: catArr,
    };
  }, [windowItems, labelField, catField, palette, catIndex]);

  /* ── Histogram buckets (for chart view) ── */
  const histogram = useMemo(() => {
    if (viewMode !== 'chart' || windowItems.length === 0) return null;
    const bucketCount = 24;
    const span = windowRange[1] - windowRange[0];
    if (span <= 0) return null;
    const bucketSize = span / bucketCount;

    const cats = Array.from(catIndex.keys());
    const hasCats = cats.length > 0;

    const buckets: { start: number; end: number; total: number; byCat: Map<string, number> }[] = [];
    for (let i = 0; i < bucketCount; i++) {
      const start = windowRange[0] + i * bucketSize;
      buckets.push({ start, end: start + bucketSize, total: 0, byCat: new Map() });
    }

    let maxCount = 0;
    let peakBucket = 0;
    for (const f of windowItems) {
      const t = new Date(String(f.properties?.[tsField] ?? '')).getTime();
      if (isNaN(t)) continue;
      const bi = Math.min(Math.floor((t - windowRange[0]) / bucketSize), bucketCount - 1);
      if (bi < 0) continue;
      const b = buckets[bi];
      b.total++;
      if (hasCats) {
        const c = String(f.properties?.[catField] ?? 'unknown').toLowerCase();
        b.byCat.set(c, (b.byCat.get(c) ?? 0) + 1);
      }
      if (b.total > maxCount) {
        maxCount = b.total;
        peakBucket = bi;
      }
    }

    return { buckets, maxCount, peakBucket, cats: hasCats ? cats : null };
  }, [viewMode, windowItems, windowRange, tsField, catField, catIndex]);

  if (!widgetConfig?.dataBinding?.boundLayerId) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Bind this widget to a Web Map layer to display an activity timeline.
        </Typography>
      </Box>
    );
  }

  if (sorted.length === 0) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Typography variant="body2" color="text.secondary">No events loaded.</Typography>
      </Box>
    );
  }

  const isHorizontal = orientation === 'horizontal';

  const sliderLabel = (val: number) => {
    const span = timeRange[1] - timeRange[0];
    return shortTime(timeRange[0] + (val / 100) * span);
  };

  const timeSlider = (
    <Box sx={{ px: 2, py: 0.5, flexShrink: 0, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, minWidth: 50 }}>
          {shortTime(windowRange[0])}
        </Typography>
        <Slider
          size="small"
          value={sliderValue}
          onChange={handleSliderChange}
          valueLabelDisplay="auto"
          valueLabelFormat={sliderLabel}
          min={0}
          max={100}
          step={0.5}
          sx={{ flex: 1 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, minWidth: 50, textAlign: 'right' }}>
          {shortTime(windowRange[1])}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: -0.25 }}>
        {windowItems.length.toLocaleString()} of {sorted.length.toLocaleString()} events in window
      </Typography>
    </Box>
  );

  /* ═══════════════════════════ CHART VIEW ═══════════════════════════ */
  if (viewMode === 'chart' && histogram) {
    const peakTime = shortTime(histogram.buckets[histogram.peakBucket]?.start);
    const avgPerBucket = Math.round(windowItems.length / histogram.buckets.length);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {/* Stats header bar */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
        >
          <Chip
            size="small"
            label={`${(windowItems.length / 1000).toFixed(1)}k events`}
            sx={{ fontWeight: 700, fontSize: 11, bgcolor: palette[0], color: '#fff' }}
          />
          {peakTime && (
            <Chip
              size="small"
              variant="outlined"
              label={`Peak: ${peakTime}`}
              sx={{ fontSize: 10, fontWeight: 600, borderColor: palette[1], color: palette[1] }}
            />
          )}
          {stats.categories.slice(0, 3).map((c) => (
            <Stack key={c.label} alignItems="center" sx={{ minWidth: 50 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: c.color, lineHeight: 1 }}>
                {c.count.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                {c.label}
              </Typography>
            </Stack>
          ))}
          <Stack alignItems="center" sx={{ minWidth: 50 }}>
            <Typography variant="subtitle2" fontWeight={700} lineHeight={1}>
              {avgPerBucket.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
              Avg/bucket
            </Typography>
          </Stack>
        </Stack>

        {/* Bar chart */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', px: 1, py: 0.5, overflow: 'hidden' }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: '2px', minHeight: 0 }}>
            {histogram.buckets.map((b, bi) => {
              const pct = histogram.maxCount > 0 ? (b.total / histogram.maxCount) * 100 : 0;
              const isPeak = bi === histogram.peakBucket;

              if (histogram.cats && histogram.cats.length > 0) {
                const segments: { cat: string; count: number; color: string }[] = [];
                histogram.cats.forEach((cat) => {
                  const count = b.byCat.get(cat) ?? 0;
                  if (count > 0) {
                    const ci = catIndex.get(cat) ?? 0;
                    segments.push({ cat, count, color: palette[ci % palette.length] });
                  }
                });

                return (
                  <Tooltip
                    key={bi}
                    title={
                      <Box>
                        <Typography variant="caption" fontWeight={600}>{shortTime(b.start)}</Typography>
                        {segments.map((s) => (
                          <Typography key={s.cat} variant="caption" display="block">
                            {s.cat}: {s.count}
                          </Typography>
                        ))}
                        <Typography variant="caption" fontWeight={600}>Total: {b.total}</Typography>
                      </Box>
                    }
                    arrow
                  >
                    <Box
                      sx={{
                        flex: 1,
                        height: `${pct}%`,
                        minHeight: b.total > 0 ? 2 : 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        borderRadius: '2px 2px 0 0',
                        overflow: 'hidden',
                        opacity: isPeak ? 1 : 0.85,
                        transition: 'opacity 0.15s',
                        '&:hover': { opacity: 1, filter: 'brightness(1.15)' },
                      }}
                    >
                      {segments.map((s) => {
                        const segPct = b.total > 0 ? (s.count / b.total) * 100 : 0;
                        return (
                          <Box
                            key={s.cat}
                            sx={{
                              height: `${segPct}%`,
                              minHeight: s.count > 0 ? 1 : 0,
                              bgcolor: s.color,
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Tooltip>
                );
              }

              return (
                <Tooltip key={bi} title={`${shortTime(b.start)} — ${b.total} events`} arrow>
                  <Box
                    sx={{
                      flex: 1,
                      height: `${pct}%`,
                      minHeight: b.total > 0 ? 2 : 0,
                      bgcolor: isPeak ? palette[2] ?? palette[0] : palette[0],
                      borderRadius: '2px 2px 0 0',
                      opacity: isPeak ? 1 : 0.75,
                      transition: 'opacity 0.15s',
                      '&:hover': { opacity: 1, filter: 'brightness(1.2)' },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>

          {/* X-axis labels */}
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.25 }}>
            {[0, Math.floor(histogram.buckets.length / 4), Math.floor(histogram.buckets.length / 2), Math.floor(histogram.buckets.length * 3 / 4), histogram.buckets.length - 1].map((bi) => (
              <Typography key={bi} variant="caption" color="text.disabled" sx={{ fontSize: 9 }}>
                {shortTime(histogram.buckets[bi]?.start)}
              </Typography>
            ))}
          </Stack>
        </Box>

        {/* Legend */}
        {histogram.cats && histogram.cats.length > 0 && (
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="center"
            flexWrap="wrap"
            sx={{ px: 1, py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}
          >
            {histogram.cats.map((cat) => {
              const ci = catIndex.get(cat) ?? 0;
              return (
                <Stack key={cat} direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: palette[ci % palette.length] }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, textTransform: 'capitalize' }}>
                    {cat}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        )}

        {timeSlider}
      </Box>
    );
  }

  /* ═══════════════════════════ TIMELINE VIEW ═══════════════════════════ */

  const statsPanel = (
    <Box
      sx={{
        ...(isHorizontal
          ? { borderTop: '1px solid', borderColor: 'divider', pt: 1, px: 1 }
          : { borderLeft: '1px solid', borderColor: 'divider', pl: 1.5, pr: 1, minWidth: 130, maxWidth: 160, flexShrink: 0 }),
        overflow: 'auto',
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mb: 0.5, display: 'block' }}>
        Stats
      </Typography>
      <Stack spacing={0.5} direction={isHorizontal ? 'row' : 'column'} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1}>{stats.totalEvents.toLocaleString()}</Typography>
          <Typography variant="caption" color="text.secondary">Events</Typography>
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1}>{stats.uniqueEntities}</Typography>
          <Typography variant="caption" color="text.secondary">Entities</Typography>
        </Box>
      </Stack>
      {stats.categories.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
            By category
          </Typography>
          <Stack spacing={0.25} direction={isHorizontal ? 'row' : 'column'} flexWrap="wrap" useFlexGap>
            {stats.categories.map((c) => (
              <Chip
                key={c.label}
                size="small"
                label={`${c.label} (${c.count})`}
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  bgcolor: `${c.color}22`,
                  color: c.color,
                  borderColor: c.color,
                  height: 20,
                }}
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );

  /* ── Vertical timeline ── */
  if (!isHorizontal) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', px: 1.5, py: 1 }}>
            {windowItems.map((f, idx) => {
              const p = f.properties ?? {};
              const label = String(p[labelField] ?? `Event ${idx + 1}`);
              const ts = p[tsField];
              const desc = descField ? p[descField] : undefined;
              const cat = catField ? String(p[catField] ?? '') : undefined;
              const color = dotColor(cat, palette, catIndex);
              const isLast = idx === windowItems.length - 1;

              return (
                <Stack key={idx} direction="row" spacing={1.5} sx={{ minHeight: 40 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16, flexShrink: 0 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0, mt: 0.5, boxShadow: `0 0 4px ${color}` }} />
                    {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', my: 0.25 }} />}
                  </Box>
                  <Box sx={{ flex: 1, pb: 1.5, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: '60%' }}>{label}</Typography>
                      {cat && <Typography variant="caption" sx={{ color, fontWeight: 600 }}>{cat}</Typography>}
                    </Stack>
                    {ts != null && <Typography variant="caption" color="text.secondary">{formatTimestamp(ts)}</Typography>}
                    {desc != null && <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>{String(desc)}</Typography>}
                  </Box>
                </Stack>
              );
            })}
          </Box>
          {statsPanel}
        </Box>
        {timeSlider}
      </Box>
    );
  }

  /* ── Horizontal timeline ── */
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1, py: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', minWidth: 'max-content', height: '100%' }}>
          {windowItems.map((f, idx) => {
            const p = f.properties ?? {};
            const label = String(p[labelField] ?? `Event ${idx + 1}`);
            const ts = p[tsField];
            const cat = catField ? String(p[catField] ?? '') : undefined;
            const color = dotColor(cat, palette, catIndex);
            const isLast = idx === windowItems.length - 1;

            return (
              <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80, maxWidth: 120, px: 0.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1, height: 2, bgcolor: idx === 0 ? 'transparent' : 'divider' }} />
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0, boxShadow: `0 0 4px ${color}` }} />
                  <Box sx={{ flex: 1, height: 2, bgcolor: isLast ? 'transparent' : 'divider' }} />
                </Box>
                <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} noWrap display="block">{label}</Typography>
                  {ts != null && <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10 }}>{shortTime(ts)}</Typography>}
                  {cat && <Typography variant="caption" sx={{ color, fontSize: 10, fontWeight: 600 }} display="block">{cat}</Typography>}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      {statsPanel}
      {timeSlider}
    </Box>
  );
};

export default ActivityTimelineWidget;
